/**
 * Centralised mapping from arbitrary errors (fetch, axios-like, native Error) to
 * user-facing toast messages. Mirrors the pattern used by EMMA's `ToastFactory`.
 */
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import type { ToastType } from '../providers/toast';

type ShowToast = (message: string, type: ToastType, autoDismiss?: boolean) => void;

type HttpLikeError = {
  status?: number;
  response?: { status?: number; data?: unknown };
  message?: string;
};

const toToastMessage = (value: unknown): string | undefined => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (value == null) return undefined;
  try {
    return JSON.stringify(value);
  } catch {
    return undefined;
  }
};

const extractStatus = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object') return undefined;
  const e = error as HttpLikeError;
  return e.status ?? e.response?.status;
};

const extractDetail = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object') return undefined;
  const e = error as HttpLikeError;
  const data = e.response?.data;
  if (data && typeof data === 'object') {
    const obj = data as { detail?: unknown; title?: unknown; message?: unknown };
    return toToastMessage(obj.detail) ?? toToastMessage(obj.title) ?? toToastMessage(obj.message);
  }
  return toToastMessage(e.message);
};

const messageForStatus = (status: number, detail?: string): string => {
  switch (status) {
    case 400:
      return detail ?? 'Bad request. Please check your input and try again.';
    case 401:
      return 'You are not signed in. Please sign in again.';
    case 403:
      return detail ?? 'You do not have permission to perform this action.';
    case 404:
      return detail ?? 'The requested resource was not found.';
    case 409:
      return detail ?? 'Conflict. The resource is in an unexpected state.';
    case 422:
      return detail ?? 'The request could not be processed.';
    case 429:
      return 'Too many requests. Please slow down and try again.';
    case 500:
      return 'Internal server error. Please contact the Labeling Platform team.';
    case 502:
    case 503:
    case 504:
      return 'The service is temporarily unavailable. Please retry shortly.';
    default:
      return detail ?? `Unexpected error (status ${status}).`;
  }
};

export const toastFromError = (error: unknown, showToast: ShowToast): void => {
  if (error == null) return;
  if (error instanceof InteractionRequiredAuthError) return;

  // Preserve previous UX for untyped/internal errors that only expose a message.
  if (error instanceof Error && /Internal S/i.test(error.message)) {
    showToast('An unexpected error occurred. Please contact the Labeling Platform Team.', 'error');
    return;
  }

  const status = extractStatus(error);
  if (status !== undefined) {
    showToast(messageForStatus(status, extractDetail(error)), 'error');
    return;
  }

  // AbortError / cancelled fetches during navigation – stay silent.
  if (error instanceof DOMException && error.name === 'AbortError') return;
  if (error instanceof Error && /aborted/i.test(error.message)) return;

  const detail = extractDetail(error);
  if (detail) {
    showToast(detail, 'error');
    return;
  }

  showToast('An unknown error occurred.', 'error');
};
